import type { BandTab } from '../hooks/useBandRoute';
import { formatDurationMs, inLast, windowSpan, type FunnelClockStat, type FunnelCounters, type FunnelWindow } from './funnel';
import { formatUtcClock } from './time';

/**
 * The view model of the Overview's Key Operating Metrics: the ten tiles in three groups, each with
 * its figure, context line, what it opens and whether it needs attention. Pure, so it is tested without
 * a screen. Components only draw it (docs/c3/DESIGN.md: Group block, Metric card).
 */

export type MetricId =
  | 'incidents'
  | 'queue'
  | 'alerts'
  | 'assign'
  | 'triage'
  | 'remediate'
  | 'resolve'
  | 'suppression'
  | 'autoResolution'
  | 'integrations';

export type GroupId = 'load' | 'timing' | 'quality';

/** Whether the counters are real: still being read for the first time, failed to read, or ready. */
export type KomState = 'ready' | 'loading' | 'error';

/** Where a tile leads: the Threat Feed in one of its views, or another tab of the band. */
export type Destination =
  | { kind: 'feed'; view: 'active' | 'suppressed' }
  | { kind: 'tab'; tab: BandTab };

export type StatusIcon = 'check' | 'hourglass' | 'alert' | 'minus';

export interface Chip {
  text: string;
  tone: 'need' | 'done';
  icon: StatusIcon;
}

export interface Change {
  direction: 'up' | 'down' | 'flat';
  /** "6.2%", "18s", "1.4 pts": the size of the change, without the direction. */
  text: string;
}

export interface KomTile {
  id: MetricId;
  label: string;
  /** "now" for a figure that is true at this moment, or the period of the counters for one measured over it. */
  scope: 'now' | FunnelWindow;
  /** null when there is nothing to show; `detail` then says why. */
  value: string | null;
  detail: string;
  /** What the detail line stands on, said on hover (the number of cases measured), not in the line. */
  detailTitle?: string;
  /** Needs a person's eyes: the tile takes the attention tint. Never the only signal (see `chip`). */
  attention: boolean;
  /** The figure is drawn in red: something is open (Active Incidents) or waits on a person (My Queue). */
  alert?: boolean;
  chip?: Chip;
  /** A second line that says what the card does, e.g. "Decide next". */
  action?: string;
  destination?: Destination;
  severity?: { id: 'crit' | 'high' | 'med' | 'low'; count: number }[];
  /** Against the previous period; absent when the backend does not send it. */
  change?: Change;
}

export interface KomGroup {
  id: GroupId;
  title: string;
  /** Absent when the group has nothing to add to what the page already says (the strip under the top bar). */
  status?: { text: string; tone: 'neutral' | 'attention'; icon: StatusIcon };
  tiles: KomTile[];
}

const n = (value: number) => value.toLocaleString('en-US');

/** "1 source", "2 sources". */
const count = (value: number, singular: string, plural = `${singular}s`) =>
  `${n(value)} ${value === 1 ? singular : plural}`;

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

function countChange(current: number, previous: number | undefined): Change | undefined {
  if (!finite(current) || !finite(previous) || previous <= 0) return undefined;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.05) return { direction: 'flat', text: '0.0%' };
  return { direction: pct > 0 ? 'up' : 'down', text: `${Math.abs(pct).toFixed(1)}%` };
}

function durationChange(current: number, previous: number | undefined): Change | undefined {
  if (!finite(current) || !finite(previous) || previous <= 0) return undefined;
  const delta = current - previous;
  if (Math.abs(delta) < 500) return { direction: 'flat', text: '0s' };
  return { direction: delta > 0 ? 'up' : 'down', text: formatDurationMs(Math.abs(delta)) };
}

function pointsChange(current: number, previous: number | undefined): Change | undefined {
  if (!finite(current) || !finite(previous) || previous < 0 || previous > 100) return undefined;
  const delta = current - previous;
  if (Math.abs(delta) < 0.05) return { direction: 'flat', text: '0.0 pts' };
  return { direction: delta > 0 ? 'up' : 'down', text: `${Math.abs(delta).toFixed(1)} pts` };
}

function pct(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null;
}

interface ClockSpec {
  id: 'assign' | 'triage' | 'remediate' | 'resolve';
  label: string;
  stat: FunnelClockStat | null;
  previous: number | undefined;
  span: string;
  missing: string;
}

/** What the tiles say instead of a figure while the counters are not real. */
function withoutFigures(groups: KomGroup[], text: string, icon: StatusIcon): KomGroup[] {
  return groups.map((group) => ({
    ...group,
    status: { text, tone: 'neutral', icon },
    tiles: group.tiles.map((tile) => ({
      ...tile,
      value: null,
      detail: text,
      attention: false,
      alert: false,
      chip: undefined,
      action: undefined,
      severity: undefined,
      change: undefined,
    })),
  }));
}

/**
 * Builds the three groups from the counters. While `state` is "loading" or "error" the counters are
 * empty placeholders, so no figure, status or attention is shown: a real zero must not be told apart
 * from a read that has not finished.
 */
export function buildKom(counters: FunnelCounters, state: KomState = 'ready'): KomGroup[] {
  const { threats, queue, events, candidates, fp, verdicts, autoResolution, integrations, clocks } = counters;
  const previous = counters.previous;
  const waiting = queue.waiting;
  const period = counters.window;
  const within = inLast(period);

  const autoPct = pct(autoResolution.autoCompleted, autoResolution.closed);
  const unhealthy = integrations.connected - integrations.healthy;
  const lag = integrations.worstLagMs != null ? ` · worst lag ${formatDurationMs(integrations.worstLagMs)}` : '';

  const load: KomTile[] = [
    {
      id: 'incidents',
      label: 'Active Incidents',
      scope: 'now',
      value: n(threats.open),
      detail:
        [
          [threats.byRisk.critical, 'critical'],
          [threats.byRisk.high, 'high'],
          [threats.byRisk.medium, 'medium'],
          [threats.byRisk.low, 'low'],
        ]
          .filter(([value]) => (value as number) > 0)
          .map(([value, word]) => `${value} ${word}`)
          .join(' · ') || 'No active incidents',
      attention: false,
      alert: threats.open > 0,
      destination: { kind: 'feed', view: 'active' },
      severity: [
        { id: 'crit', count: threats.byRisk.critical },
        { id: 'high', count: threats.byRisk.high },
        { id: 'med', count: threats.byRisk.medium },
        { id: 'low', count: threats.byRisk.low },
      ],
    },
    {
      id: 'queue',
      label: 'My Queue',
      scope: 'now',
      value: n(waiting),
      detail:
        waiting > 0
          ? `Awaiting your decision${queue.oldestWaitMs != null ? ` · oldest waiting ${formatDurationMs(queue.oldestWaitMs)}` : ''}`
          : 'Nothing is waiting for your decision',
      attention: waiting > 0,
      alert: waiting > 0,
      action: waiting > 0 ? 'Decide next' : undefined,
      destination: { kind: 'feed', view: 'active' },
    },
    {
      id: 'alerts',
      label: 'Alerts Ingested',
      scope: period,
      value: n(events.total),
      detail:
        events.total === 0
          ? `No alerts arrived ${within}`
          : [
              integrations.connected > 0 ? count(integrations.connected, 'source') : null,
              candidates.pending > 0 ? `${n(candidates.pending)} in transit` : null,
              events.duplicate > 0 ? `${n(events.duplicate)} deduplicated` : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'Nothing in transit',
      attention: false,
      destination: { kind: 'tab', tab: 'flow' },
      change: countChange(events.total, previous?.alerts),
    },
  ];

  // The clocks are measured over the remediations that finished (completed or reverted) in the period.
  const clockSpecs: ClockSpec[] = [
    {
      id: 'assign',
      label: 'Mean Time to Assign',
      stat: clocks.assign,
      previous: previous?.assignMs,
      span: 'formed → assigned',
      missing: `No remediation finished ${within}`,
    },
    {
      id: 'triage',
      label: 'Mean Time to Triage',
      stat: clocks.triage,
      previous: previous?.triageMs,
      span: waiting > 0 ? `gated only · ${n(waiting)} waiting now` : 'gated only',
      missing: `No gated remediation finished ${within}`,
    },
    {
      id: 'remediate',
      label: 'Mean Time to Remediate',
      stat: clocks.remediate,
      previous: previous?.remediateMs,
      span: 'decided → verified',
      missing: `No remediation finished ${within}`,
    },
    {
      id: 'resolve',
      label: 'Mean Time to Close',
      stat: clocks.resolve,
      previous: previous?.resolveMs,
      span: 'first alert → verified',
      missing: `No remediation finished ${within}`,
    },
  ];

  const timing: KomTile[] = clockSpecs.map((spec) => {
    return {
      id: spec.id,
      label: spec.label,
      scope: period,
      value: spec.stat ? formatDurationMs(spec.stat.medianMs) : null,
      detail: spec.stat
        ? `p90 ${formatDurationMs(spec.stat.p90Ms)} · ${spec.span}`
        : spec.missing,
      detailTitle: spec.stat ? `Measured over ${count(spec.stat.n, 'remediation')}` : undefined,
      attention: false,
      change: spec.stat ? durationChange(spec.stat.medianMs, spec.previous) : undefined,
    };
  });

  const quality: KomTile[] = [
    {
      id: 'suppression',
      label: 'False Positive Suppression',
      scope: period,
      value: n(fp.suppressed),
      detail: `${count(fp.anomalies, 'anomaly', 'anomalies')} → ${count(fp.threats, 'threat')}${verdicts.escalated > 0 ? ` · ${n(verdicts.escalated)} escalated` : ''}`,
      attention: false,
      destination: { kind: 'feed', view: 'suppressed' },
      change: countChange(fp.suppressed, previous?.suppressed),
    },
    {
      id: 'autoResolution',
      label: 'Auto-Resolution Rate',
      scope: period,
      value: autoPct != null ? `${autoPct.toFixed(1)}%` : null,
      detail:
        autoPct != null
          ? `${n(autoResolution.autoCompleted)} auto / ${n(autoResolution.closed)} closed${autoResolution.gatedCompleted > 0 ? ` · ${n(autoResolution.gatedCompleted)} gated` : ''}`
          : `No remediation was closed ${within}`,
      attention: false,
      destination: { kind: 'tab', tab: 'pipe' },
      change: autoPct != null ? pointsChange(autoPct, previous?.autoResolutionPct) : undefined,
    },
    {
      id: 'integrations',
      label: 'Integration Health',
      scope: 'now',
      value: integrations.connected > 0 ? `${integrations.healthy} / ${integrations.connected}` : null,
      // "Healthy" is a connected source that is active and reports no error.
      detail:
        integrations.connected === 0
          ? 'No source is connected yet'
          : unhealthy > 0
            ? `${unhealthy} not healthy${lag}`
            : `All sources healthy${lag}`,
      attention: unhealthy > 0,
      chip:
        integrations.connected === 0
          ? undefined
          : unhealthy > 0
            ? { text: `${unhealthy} unhealthy`, tone: 'need', icon: 'alert' }
            : { text: 'Healthy', tone: 'done', icon: 'check' },
      action:
        integrations.connected > 0
          ? integrations.connected === 1
            ? 'See the source'
            : `See each of the ${n(integrations.connected)} sources`
          : undefined,
      destination: { kind: 'tab', tab: 'src' },
    },
  ];

  const measured = clockSpecs.filter((spec) => spec.stat != null).length;

  const groups: KomGroup[] = [
    {
      id: 'load',
      title: 'Load',
      // While something waits, the strip under the top bar and the My Queue tile already say so.
      status: waiting > 0 ? undefined : { text: 'Nothing waiting on you', tone: 'neutral', icon: 'check' },
      tiles: load,
    },
    {
      id: 'timing',
      title: 'Timing',
      status:
        measured === 0
          ? { text: 'Nothing measured yet', tone: 'neutral', icon: 'minus' }
          : {
              text: `${measured} of ${clockSpecs.length} measured`,
              tone: 'neutral',
              icon: measured === clockSpecs.length ? 'check' : 'minus',
            },
      tiles: timing,
    },
    {
      id: 'quality',
      title: 'Quality',
      status:
        integrations.connected === 0
          ? { text: 'No sources connected', tone: 'neutral', icon: 'minus' }
          : unhealthy > 0
            ? { text: `${unhealthy} ${unhealthy === 1 ? 'source' : 'sources'} not healthy`, tone: 'attention', icon: 'alert' }
            : { text: 'All sources healthy', tone: 'neutral', icon: 'check' },
      tiles: quality,
    },
  ];

  if (state === 'loading') return withoutFigures(groups, 'Loading…', 'minus');
  if (state === 'error') return withoutFigures(groups, 'Could not be loaded', 'alert');
  return groups;
}

/** What follows the period picker: "changes compare with the previous 24 hours · as of 14:32 UTC". */
export function komContext(counters: FunnelCounters): string {
  const parts: string[] = [];
  if (counters.previous) parts.push(`changes compare with the previous ${windowSpan(counters.window)}`);
  const asOf = new Date(counters.asOf);
  if (!Number.isNaN(asOf.getTime())) parts.push(`as of ${formatUtcClock(asOf)} UTC`);
  return parts.join(' · ');
}

const TAB_NAMES: Record<BandTab, string> = {
  kom: 'Key Operating Metrics',
  pipe: 'the AI Analysis Pipeline',
  flow: 'Alert Flow',
  src: 'Connected Sources',
};

/** What a tile does when pressed, for a screen reader: "opens the Threat Feed". */
export function destinationLabel(destination: Destination): string {
  if (destination.kind === 'feed') {
    return destination.view === 'suppressed' ? 'opens the Resolved view of the Threat Feed' : 'opens the Threat Feed';
  }
  return `opens ${TAB_NAMES[destination.tab]}`;
}
